// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon, QuestionCircleIcon } from '@calendly/ui/components/icons';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { colorTextSecondary } from '@calendly/ui/theme';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetPolicyQuery } from 'common';
import LoadingState from 'components/core/loading_state';
import { Container } from 'components/organizations/layout';
import { LearnMoreButton, OccupancyRatio, PageHeaderWithTabs, TabContainer, TabContent } from 'components/page_elements';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { getOverviewHelpCenterLink, useContextualHelp } from 'features/contextual_help';
import { CONTENT_PAGES, useNewEmptyStates } from 'features/empty_state';
import { SharedEventTypes, Workflows, Invitations } from 'features/organization_settings';
import { fetchStatistics, statisticsSelector } from 'features/organization_statistics';
import { UpgradeBanner } from 'features/upgrade_banner';
import { NewUserGroupButton, isUserGroupsEnabledSelector } from 'features/user_groups';
import { ActiveUsers, AddNewUser, MigratingUsers, PendingUsers, totalInvitationCountSelector, totalMigrationCountSelector, totalUserCountSelector } from 'features/users_page';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { toSentenceCase } from 'shared/utils/to_sentence_case';
import { mergeSettingsSearch, parseSettingsTabFromSearch, SETTINGS_SECTION } from './constants';
import { guardSettingsModalTabNavigation } from './guard_settings_modal_tabs';
import { useIsSettingsModalLayout } from './layout_context';
import { SettingsModalUserGroupsShell } from './settings_modal_user_groups_shell';
const activeUsersToolbarCss = "a1oxhz93";
const activeUsersActionsCss = "a1vt73wk";
const addCounterToString = (title, counter) => {
  if (typeof counter !== 'number') {
    return title;
  }
  return `${title} (${counter})`;
};
const SharedEventTypesTab = () => <TabContainer narrow>
    <TabContent>
      <TitleText level={2} size="small">
        {I18n.t('publisher.organization_settings.shared_event_types.subtitle')}
      </TitleText>
      <SharedEventTypes />
    </TabContent>
  </TabContainer>;
const WorkflowsTab = () => <TabContainer narrow>
    <TabContent>
      <TitleText level={2} size="small">
        {I18n.t('publisher.organization_settings.workflows.subtitle')}
      </TitleText>
      <Workflows />
    </TabContent>
  </TabContainer>;
const InvitationsPermissionsTab = () => <TabContainer narrow>
    <TabContent>
      <Invitations />
    </TabContent>
  </TabContainer>;
const useSettingsModalPeopleTabs = () => {
  const location = useLocation();
  const isOwnerOrAdmin = useSelector(isAdminOrOwnerSelector);
  const usersCount = useSelector(totalUserCountSelector);
  const invitationsCount = useSelector(totalInvitationCountSelector);
  const migrationsCount = useSelector(totalMigrationCountSelector);
  const hasAppConsolidationFeature = useFlag('calendly_app_consolidation');
  const isWorkflowsAppShellRebrand = useProductRefreshAppShell();
  const {
    data: policyData = {},
    isFetching: isPolicyFetching
  } = useGetPolicyQuery();
  const {
    canManageInvitationPermissions,
    canManageOrganizationEventTypeSettings,
    canManageWorkflows
  } = policyData || {};
  const rawTab = parseSettingsTabFromSearch(location.search);
  const allowedSlugs = useMemo(() => {
    const base = ['active_users', 'pending_users'];
    const migrating = hasAppConsolidationFeature ? ['migrating_users'] : [];
    const rest = ['groups'];
    const perm = [];
    if (isOwnerOrAdmin && canManageOrganizationEventTypeSettings) perm.push('event_types');
    if (isOwnerOrAdmin && canManageWorkflows) perm.push('workflows');
    if (isOwnerOrAdmin && canManageInvitationPermissions) perm.push('invitations');
    return [...base, ...migrating, ...rest, ...perm];
  }, [canManageInvitationPermissions, canManageOrganizationEventTypeSettings, canManageWorkflows, hasAppConsolidationFeature, isOwnerOrAdmin]);
  const effectiveTab = allowedSlugs.includes(rawTab ?? '') ? rawTab : allowedSlugs[0];
  const tabs = useMemo(() => {
    const rows = [{
      slug: 'active_users',
      title: addCounterToString(I18n.t('publisher.admin.people.subtabs.active_users'), usersCount)
    }, {
      slug: 'pending_users',
      title: addCounterToString(I18n.t('publisher.admin.people.subtabs.pending_users'), invitationsCount)
    }];
    if (hasAppConsolidationFeature) {
      rows.push({
        slug: 'migrating_users',
        title: addCounterToString(I18n.t('publisher.admin.people.subtabs.migrating_users'), migrationsCount)
      });
    }
    rows.push({
      slug: 'groups',
      title: I18n.t('publisher.admin.people.subtabs.groups')
    });
    const permissionRows = [];
    if (isOwnerOrAdmin && canManageOrganizationEventTypeSettings) {
      permissionRows.push({
        slug: 'event_types',
        title: I18n.t('publisher.admin.people.subtabs.event_types_permissions')
      });
    }
    if (isOwnerOrAdmin && canManageWorkflows) {
      permissionRows.push({
        slug: 'workflows',
        title: I18n.t(isWorkflowsAppShellRebrand ? 'publisher.workflows_app_shell_rebrand.admin.people.subtabs.workflows_permissions' : 'publisher.admin.people.subtabs.workflows_permissions')
      });
    }
    if (isOwnerOrAdmin && canManageInvitationPermissions) {
      permissionRows.push({
        slug: 'invitations',
        title: I18n.t('publisher.admin.people.subtabs.invitations_permissions')
      });
    }
    return [...rows, ...permissionRows].filter(r => allowedSlugs.includes(r.slug)).map(r => ({
      key: r.slug,
      title: r.title,
      route: {
        pathname: location.pathname,
        search: mergeSettingsSearch(SETTINGS_SECTION.adminPeople, location.search, r.slug),
        hash: location.hash
      },
      selected: effectiveTab === r.slug
    }));
  }, [allowedSlugs, canManageInvitationPermissions, canManageOrganizationEventTypeSettings, canManageWorkflows, effectiveTab, hasAppConsolidationFeature, invitationsCount, isOwnerOrAdmin, isWorkflowsAppShellRebrand, location.hash, location.pathname, location.search, migrationsCount, usersCount]);
  return {
    effectiveTab,
    isPolicyFetching,
    location,
    rawTab,
    tabs
  };
};
export const SettingsModalPeopleHeader = ({
  onClose,
  showCloseInHeader = true,
  userGroupsMemoryHistory
}) => {
  const isNewEmptyStatesEnabled = useNewEmptyStates();
  const {
    occupancyRatio
  } = useSelector(statisticsSelector);
  const {
    effectiveTab,
    isPolicyFetching,
    tabs
  } = useSettingsModalPeopleTabs();
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const isUserGroupsEnabled = useSelector(isUserGroupsEnabledSelector);
  const usersHelpCenterUrl = useMemo(() => getOverviewHelpCenterLink(CONTENT_PAGES.users), []);
  if (isPolicyFetching) {
    return null;
  }
  const closeAction = showCloseInHeader ? <IconButton key="settings-modal-close" type="button" title={I18n.t('publisher.action.close')} aria-label={I18n.t('publisher.action.close')} onClick={onClose}>
      <CloseIcon />
    </IconButton> : null;
  const activeUsersToolbarActions = effectiveTab === 'active_users' ? [...(isNewEmptyStatesEnabled && usersHelpCenterUrl ? [<LearnMoreButton key="people-help-learn-more" as="a" href={usersHelpCenterUrl} target="_blank" rel="noopener noreferrer" aria-label={I18n.t('publisher.contextual_help.page_title.adding_users_overview')}>
        <QuestionCircleIcon />
      </LearnMoreButton>] : []), <AddNewUser key="settings-modal-add-new-user" />] : [];
  const userGroupsToolbarActions = effectiveTab === 'groups' && isAdminOrOwner ? [<NewUserGroupButton key="settings-modal-new-user-group" dataTestId="settings-modal-new-user-group-button" disabled={!isUserGroupsEnabled} onSuccessNavigate={userGroupsMemoryHistory ? path => userGroupsMemoryHistory.push(path) : undefined} />] : [];
  return <PageHeaderWithTabs wide title={toSentenceCase(I18n.t('publisher.admin.navigation.people'))} description={effectiveTab === 'active_users' ? <OccupancyRatio ratio={occupancyRatio} isAdminIa /> : undefined} tabs={guardSettingsModalTabNavigation(tabs)} hasBottomMargin={false} actions={[...activeUsersToolbarActions, ...userGroupsToolbarActions, ...(closeAction ? [closeAction] : [])]} />;
};
export const SettingsModalPeoplePage = ({
  userGroupsMemoryHistory
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const inSettingsModal = useIsSettingsModalLayout();
  const isNewEmptyStatesEnabled = useNewEmptyStates();
  const {
    occupancyRatio
  } = useSelector(statisticsSelector);
  const {
    open: openContextualHelp
  } = useContextualHelp();
  const {
    effectiveTab,
    isPolicyFetching,
    location,
    rawTab,
    tabs
  } = useSettingsModalPeopleTabs();
  const usersHelpCenterUrl = useMemo(() => getOverviewHelpCenterLink(CONTENT_PAGES.users), []);
  useEffect(() => {
    if (rawTab === effectiveTab || rawTab == null) {
      return;
    }
    history.replace({
      pathname: location.pathname,
      search: mergeSettingsSearch(SETTINGS_SECTION.adminPeople, location.search, effectiveTab),
      hash: location.hash
    });
  }, [effectiveTab, history, location.hash, location.pathname, location.search, rawTab]);
  useEffect(() => {
    dispatch(fetchStatistics());
  }, [dispatch, location.pathname]);
  if (isPolicyFetching) {
    return <LoadingState />;
  }
  return <>
      {!inSettingsModal && <PageHeaderWithTabs eyebrow={<BodyText weight="bold" color={colorTextSecondary}>
              {I18n.t('publisher.admin.navigation.organization')}
            </BodyText>} title={I18n.t('publisher.admin.navigation.people')} tabs={tabs} />}
      {effectiveTab === 'active_users' && <Container>
          {isNewEmptyStatesEnabled && <UpgradeBanner featureKey="organization_invitation" contentKey={CONTENT_PAGES.users} />}
          {!inSettingsModal && <div className={activeUsersToolbarCss}>
              <OccupancyRatio ratio={occupancyRatio} isAdminIa />
              <div className={activeUsersActionsCss}>
                {isNewEmptyStatesEnabled && (usersHelpCenterUrl ? <LearnMoreButton as="a" href={usersHelpCenterUrl} target="_blank" rel="noopener noreferrer" aria-label={I18n.t('publisher.contextual_help.page_title.adding_users_overview')}>
                      <QuestionCircleIcon />
                    </LearnMoreButton> : <LearnMoreButton onClick={() => openContextualHelp({
            contentKey: CONTENT_PAGES.users
          })} aria-label={I18n.t('publisher.contextual_help.page_title.adding_users_overview')}>
                      <QuestionCircleIcon />
                    </LearnMoreButton>)}
                <AddNewUser />
              </div>
            </div>}
          <ActiveUsers />
        </Container>}
      {effectiveTab === 'pending_users' && <Container>
          <PendingUsers />
        </Container>}
      {effectiveTab === 'migrating_users' && <Container>
          <MigratingUsers />
        </Container>}
      {effectiveTab === 'groups' && <SettingsModalUserGroupsShell memoryHistory={userGroupsMemoryHistory} />}
      {effectiveTab === 'event_types' && <SharedEventTypesTab />}
      {effectiveTab === 'workflows' && <WorkflowsTab />}
      {effectiveTab === 'invitations' && <InvitationsPermissionsTab />}
    </>;
};

require("./settings_modal_people_page.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./settings_modal_people_page.js");